
import { participantLegalName } from "./breakage-participant-display";

const minor = (moneyAmount) => Number(moneyAmount?.amountMinor || 0);

const daysUntil = (isoDate, now = new Date()) => {
  if (!isoDate) {
    return null;
  }
  const ms = new Date(isoDate).getTime() - now.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

export const buildAgingBuckets = (lots = [], now = new Date()) => {
  const buckets = [
    { key: "expired", label: "Vencido", days: null, count: 0, amountMinor: 0 },
    { key: "d0_30", label: "0-30 dias", days: 30, count: 0, amountMinor: 0 },
    { key: "d31_60", label: "31-60 dias", days: 60, count: 0, amountMinor: 0 },
    { key: "d61_90", label: "61-90 dias", days: 90, count: 0, amountMinor: 0 },
    { key: "d90_plus", label: "90+ dias", days: null, count: 0, amountMinor: 0 },
  ];

  lots.forEach((lot) => {
    const remaining = minor(lot.remainingAmount);
    const d = daysUntil(lot.expiresAt, now);
    let bucket = buckets[4];
    if (d === null) {
      bucket = buckets[4];
    } else if (d < 0 || lot.agingCondition === "EXPIRED") {
      bucket = buckets[0];
    } else if (d <= 30) {
      bucket = buckets[1];
    } else if (d <= 60) {
      bucket = buckets[2];
    } else if (d <= 90) {
      bucket = buckets[3];
    } else {
      bucket = buckets[4];
    }
    bucket.count += 1;
    bucket.amountMinor += remaining;
  });

  return buckets;
};

export const buildSparklineSeries = (lots = [], weeks = 8) => {
  const now = new Date();
  const series = Array.from({ length: weeks }, () => 0);

  lots.forEach((lot) => {
    if (!lot.creditedAt) {
      return;
    }
    const credited = new Date(lot.creditedAt);
    const diffDays = Math.floor((now - credited) / (1000 * 60 * 60 * 24));
    const weekIndex = weeks - 1 - Math.floor(diffDays / 7);
    if (weekIndex >= 0 && weekIndex < weeks) {
      series[weekIndex] += minor(lot.remainingAmount) / 100;
    }
  });

  if (series.every((v) => v === 0)) {
    return series.map((_, i) => (i + 1) * 0.01);
  }
  return series;
};

export const filterLotsSparkline = (lots, predicate) =>
  buildSparklineSeries(lots.filter(predicate));

export const getSparklineTrend = (sparkline = []) => {
  if (!Array.isArray(sparkline) || sparkline.length < 2) {
    return { direction: "flat", delta: 0 };
  }
  const prev = Number(sparkline[sparkline.length - 2] || 0);
  const curr = Number(sparkline[sparkline.length - 1] || 0);
  const delta = curr - prev;
  if (Math.abs(delta) < 0.0001) {
    return { direction: "flat", delta: 0 };
  }
  return {
    direction: delta > 0 ? "up" : "down",
    delta,
  };
};

export const aggregateBreakageLots = (lots = []) => {
  const byEligibility = {
    ELIGIBLE: { count: 0, amountMinor: 0 },
    INELIGIBLE: { count: 0, amountMinor: 0 },
  };
  const byAging = {
    EXPIRED: { count: 0, amountMinor: 0 },
    WITHIN_WINDOW: { count: 0, amountMinor: 0 },
  };
  const byOrigin = {};
  const byParticipant = {};

  lots.forEach((lot) => {
    const remaining = minor(lot.remainingAmount);

    const el = lot.eligibility || "ELIGIBLE";
    if (!byEligibility[el]) {
      byEligibility[el] = { count: 0, amountMinor: 0 };
    }
    byEligibility[el].count += 1;
    byEligibility[el].amountMinor += remaining;

    const aging = lot.agingCondition || "WITHIN_WINDOW";
    if (!byAging[aging]) {
      byAging[aging] = { count: 0, amountMinor: 0 };
    }
    byAging[aging].count += 1;
    byAging[aging].amountMinor += remaining;

    const origin = lot.originType || "OTHER";
    if (!byOrigin[origin]) {
      byOrigin[origin] = { count: 0, amountMinor: 0 };
    }
    byOrigin[origin].count += 1;
    byOrigin[origin].amountMinor += remaining;

    const pid = lot.participantId || "—";
    if (!byParticipant[pid]) {
      byParticipant[pid] = {
        participantId: pid,
        legalName: participantLegalName(lot),
        lots: 0,
        amountMinor: 0,
        nextExpiresAt: lot.expiresAt || null,
      };
    }
    if (!byParticipant[pid].legalName) {
      byParticipant[pid].legalName = participantLegalName(lot);
    }
    byParticipant[pid].lots += 1;
    byParticipant[pid].amountMinor += remaining;
    if (
      lot.expiresAt &&
      (!byParticipant[pid].nextExpiresAt ||
        new Date(lot.expiresAt) < new Date(byParticipant[pid].nextExpiresAt))
    ) {
      byParticipant[pid].nextExpiresAt = lot.expiresAt;
    }
  });

  const topParticipants = Object.values(byParticipant)
    .sort((a, b) => b.amountMinor - a.amountMinor)
    .slice(0, 5);

  const originMix = Object.entries(byOrigin)
    .map(([originType, stats]) => ({ originType, ...stats }))
    .sort((a, b) => b.amountMinor - a.amountMinor);

  const recentLots = [...lots]
    .sort((a, b) => new Date(b.creditedAt) - new Date(a.creditedAt))
    .slice(0, 5);

  const agingBuckets = buildAgingBuckets(lots);

  return {
    byEligibility,
    byAging,
    agingBuckets,
    originMix,
    topParticipants,
    recentLots,
    totalLots: lots.length,
    sparklines: {
      total: buildSparklineSeries(lots),
      eligible: filterLotsSparkline(lots, (l) => l.eligibility === "ELIGIBLE"),
      expired: filterLotsSparkline(
        lots,
        (l) => l.agingCondition === "EXPIRED"
      ),
      ineligible: filterLotsSparkline(
        lots,
        (l) => l.eligibility === "INELIGIBLE"
      ),
      within: filterLotsSparkline(
        lots,
        (l) => l.agingCondition === "WITHIN_WINDOW"
      ),
      lotsCount: buildSparklineSeries(
        lots.map((l) => ({
          ...l,
          remainingAmount: { amountMinor: 100, currency: "BRL" },
        }))
      ),
    },
  };
};
